import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { getApiHost, getQueryFetchOptions, parseHttpResponse } from '../utils';

export const RECENTLY_ORDERED_QUERY_KEY = 'recentlyOrderedItems';

const RECENTLY_ORDERED_API_VERSION = '5.0';

type RecentlyOrderedQueryParams = {
  restaurantId: string;
  userId?: string | null;
  when?: string | null;
  channel?: string | null;
  isCatering?: boolean;
  enabled?: boolean;
};

export const useRecentlyOrderedQuery: (
  params: RecentlyOrderedQueryParams
) => UseQueryResult<RecentlyOrderedItems, Error> = ({
  restaurantId,
  userId,
  when,
  channel,
  isCatering,
  enabled = true,
}) => {
  return useQuery<RecentlyOrderedItems>({
    enabled: enabled && !!restaurantId && !!userId,
    queryKey: [
      RECENTLY_ORDERED_QUERY_KEY,
      restaurantId,
      userId,
      when,
      channel,
      isCatering,
    ],
    queryFn: async () => {
      const apiHost = getApiHost();
      const url = new URL(
        `/api/customer/me/restaurant/${restaurantId}/recently-ordered`,
        apiHost
      );

      const params = new URLSearchParams();
      if (when) params.set('requested_time', when);
      if (channel) params.set('cn_channel', channel);
      if (isCatering) params.set('is_catering', 'true');
      url.search = params.toString();

      const response = await fetch(
        url,
        getQueryFetchOptions(RECENTLY_ORDERED_API_VERSION)
      );

      return parseHttpResponse(response);
    },
  });
};

type RecentlyOrderedMenuModifier = {
  id: string;
  name: string;
  x_id: string;
  price: number;
  max_qty: number;
  min_qty: number;
  is_default: boolean;
  is_taxable: boolean;
  description: string;
  modifier_categories: string[];
};

type RecentlyOrderedUserSelectionModifier = {
  id?: string;
  modifier_categories?: RecentlyOrderedUserSelectionModifierCategory[];
};

type RecentlyOrderedUserSelectionModifierCategory = {
  modifiers?: RecentlyOrderedUserSelectionModifier[];
  selections?: RecentlyOrderedUserSelectionModifier[];
};

type RecentlyOrderedMenuItemImage = {
  base_url: string;
  cropped_url: string;
  file_key: string;
  gravity_x: number;
  gravity_y: number;
};

type RecentlyOrderedMenuItemBase = {
  id: string;
  name: string;
  size: string;
  x_id: string;
  image: RecentlyOrderedMenuItemImage | null;
  price: number;
  is_meta: boolean;
  description: string;
  delivery_charge: number;
  modifier_categories?: string[];
};

type RecentlyOrderedMenuItem = RecentlyOrderedMenuItemBase & {
  meta_item_details?: {
    serving_size_group: {
      child_items: RecentlyOrderedMenuItemBase[];
      default_id: string;
    };
  };
  user_selection: {
    selected_size_id?: string;
    modifier_categories: RecentlyOrderedUserSelectionModifierCategory[];
    quantity: number;
    special_instructions: string;
    per_item_total: number;
    item_sum_total: number;
  };
};

export type RecentlyOrderedItems = {
  menu_categories?: {
    id: string;
    name: string;
    items: RecentlyOrderedMenuItem[];
  }[];
  modifiers?: RecentlyOrderedMenuModifier[];
};
